﻿// src/provider/DataContextProvider.tsx
import { useAtom } from "jotai";
import React, { createContext, useEffect, useState } from "react";
import { FCC } from "types";

import { safeJsonParse } from "@/hooks/useToken";
import {
  dataContextAtom,
  DataContextInterface,
  initialDataContext,
} from "@/utils/atoms";

type DataContextType = {
  dataContext: DataContextInterface;
  setDataContext: React.Dispatch<React.SetStateAction<DataContextInterface>>;
};

export const clearContext = () => {
  try {
    localStorage.removeItem("dataContext");
  } catch (e) {
    console.error("Failed to remove dataContext from localStorage", e);
  }
};

/**
 * DO NOT hydrate synchronously.
 * Jotai atoms will hydrate themselves after mount.
 */
export const DataContext = createContext<DataContextType>({
  dataContext: initialDataContext,
  setDataContext: () => undefined,
});

export const DataContextProvider: FCC = ({ children }) => {
  // Atom is the SINGLE source of truth (persistent, thanks to atomWithStorage)
  const [stateMemory, setStateMemory] = useAtom(dataContextAtom);

  const [hydrated, setHydrated] = useState(false);

  /**
   * Hydrate from localStorage AFTER mount.
   * This avoids the race between:
   * - atoms hydrating
   * - components reading DataContext (from local storage)
   */
  useEffect(() => {
    try {
      const raw = localStorage.getItem("dataContext") || "{}";
      const [err, parsed] = safeJsonParse(raw);

      if (!err && parsed && typeof parsed === "object") {
        setStateMemory((prev) => ({
          ...prev,
          ...parsed,
          isEmailVerified:
            typeof parsed.isEmailVerified === "boolean"
              ? parsed.isEmailVerified
              : (prev.isEmailVerified ?? false),
          allowedMaxOnboardingStep:
            parsed.allowedMaxOnboardingStep ??
            prev.allowedMaxOnboardingStep ??
            1,
        }));
      }
    } catch (e) {
      console.error("Failed to read dataContext from localStorage", e);
    }

    setHydrated(true);
  }, [setStateMemory]);

  /**
   * Persist on every change
   */
  useEffect(() => {
    if (hydrated) {
      try {
        localStorage.setItem("dataContext", JSON.stringify(stateMemory));
      } catch (e) {
        console.error("Failed to save dataContext to localStorage", e);
      }
    }
  }, [stateMemory, hydrated]);

  // prevent children rendering until hydration is complete
  if (!hydrated) {
    return null;
  }

  return (
    <DataContext.Provider
      value={{
        dataContext: stateMemory,
        setDataContext: setStateMemory,
      }}
    >
      {children}
    </DataContext.Provider>
  );
};
